/**
 * Bundler app integration (custom-bundler.js).
 *
 * Associates variants to Bundler rows via custom.bundle-name metafield matching
 * .bndlr-container[data-bundle-name="..."]. Hides buy form and sticky ATC when a matching
 * bundle exists, and toggles blocks with data-show-if="bundler_visible".
 */
const BundlerIntegration = class {
  static targetId = 'bundler-target-element';

  static wrapperSelector = '.bundler-target-element';

  static containerSelector = '.bndlr-container';

  static visibleBlockSelector = '[data-show-if="bundler_visible"]';

  /**
   * @param {Element} productInfo - Product info container (#ProductInfo-*).
   */
  constructor(productInfo) {
    this.productInfo = productInfo;
    this.productForm = productInfo.closest('product-form');
    if (!this.productForm) return;

    this.syncPending = false;
    this.bundlerObserver = null;

    this.boundHandleVariantChange = this.handleVariantChange.bind(this);
    this.productForm.addEventListener('on:variant:change', this.boundHandleVariantChange);

    this.syncState();
    this.observeBundlerTarget();
  }

  /**
   * @returns {boolean}
   */
  hasBundlesEnabled() {
    return this.productForm.dataset.hasBundles === 'true';
  }

  /**
   * @returns {?Element}
   */
  getVariantPicker() {
    return this.productForm.querySelector('variant-picker');
  }

  /**
   * @returns {?Element}
   */
  getSubmitForm() {
    return this.productForm.querySelector('form.js-product-form');
  }

  /**
   * @returns {?object}
   */
  getVariantBundleMap() {
    const productId = this.getSubmitForm()?.dataset.productId;
    const mapEl = document.querySelector(`script[data-variant-bundle-map]#variant-bundle-map-${productId}`)
      || document.querySelector('script[data-variant-bundle-map]');
    if (!mapEl) return null;

    try {
      return JSON.parse(mapEl.textContent);
    } catch (error) {
      console.log(error); // eslint-disable-line
      return null;
    }
  }

  /**
   * @param {Element} picker - variant-picker element.
   * @returns {string}
   */
  getBundleNameFromPicker(picker) {
    if (!picker) return '';

    const checked = picker.querySelector('input.js-option:checked');
    if (checked?.dataset.bundle) return checked.dataset.bundle;

    const selected = picker.querySelector('.custom-select__option[aria-selected="true"]');
    if (selected?.dataset.bundle) return selected.dataset.bundle;

    return '';
  }

  /**
   * @returns {string}
   */
  getBundleName() {
    const fromPicker = this.getBundleNameFromPicker(this.getVariantPicker());
    if (fromPicker) return fromPicker;

    const variantId = this.getSubmitForm()?.querySelector('input[name="id"]')?.value;
    const map = this.getVariantBundleMap();
    if (map && variantId) {
      return map[String(variantId)] || '';
    }

    return '';
  }

  /**
   * @param {Element} wrapper
   * @param {string} bundleName
   * @returns {boolean}
   */
  wrapperMatchesBundleName(wrapper, bundleName) {
    if (!bundleName) return false;

    return [...wrapper.querySelectorAll(BundlerIntegration.containerSelector)].some(
      (container) => container.getAttribute('data-bundle-name') === bundleName
    );
  }

  /**
   * @param {Element} el
   * @param {boolean} visible
   */
  setBundlerElementVisible(el, visible) {
    if (!el) return;

    if (visible) {
      el.hidden = false;
      if (el.id === BundlerIntegration.targetId) {
        el.style.display = 'block';
      } else {
        el.style.removeProperty('display');
      }
      el.style.removeProperty('visibility');
      return;
    }

    el.hidden = true;
    if (el.id === BundlerIntegration.targetId) {
      el.style.display = 'none';
    }
  }

  /**
   * @param {string} bundleName
   * @returns {boolean} Whether a matching bundle row is visible.
   */
  syncBundleVisibility(bundleName) {
    const target = document.getElementById(BundlerIntegration.targetId);
    const wrappers = [...document.querySelectorAll(BundlerIntegration.wrapperSelector)];

    wrappers.forEach((wrapper) => this.setBundlerElementVisible(wrapper, false));
    this.setBundlerElementVisible(target, false);

    if (!bundleName) return false;

    let hasMatch = false;
    wrappers.forEach((wrapper) => {
      const matches = this.wrapperMatchesBundleName(wrapper, bundleName);
      const hasContainer = wrapper.querySelector(BundlerIntegration.containerSelector) != null;
      if (matches && hasContainer) {
        this.setBundlerElementVisible(wrapper, true);
        hasMatch = true;
      }
    });

    if (hasMatch) this.setBundlerElementVisible(target, true);

    return hasMatch;
  }

  /**
   * @param {boolean} active
   */
  syncBuyForm(active) {
    this.productInfo.querySelectorAll('form.js-product-form').forEach((form) => {
      if (form.closest('sticky-atc')) return;
      form.hidden = active;
    });
  }

  /**
   * @param {boolean} active
   */
  syncBundlerVisibleBlocks(active) {
    const inDesignMode = window.Shopify && Shopify.designMode;
    this.productForm.querySelectorAll(BundlerIntegration.visibleBlockSelector).forEach((el) => {
      el.hidden = inDesignMode ? false : !active;
    });
  }

  /**
   * @param {boolean} active
   */
  syncStickyAtc(active) {
    const sticky = this.productForm.querySelector('sticky-atc');
    if (!sticky) return;
    sticky.hidden = active;
  }

  /**
   * Sync bundle row visibility and related product form UI.
   */
  syncState() {
    try {
      const bundleName = this.hasBundlesEnabled() ? this.getBundleName() : '';
      const active = this.syncBundleVisibility(bundleName);
      this.syncBuyForm(active);
      this.syncBundlerVisibleBlocks(active);
      this.syncStickyAtc(active);
    } catch (error) {
      console.log(error); // eslint-disable-line
    }
  }

  /**
   * Observe Bundler app injections and re-sync when markup changes.
   */
  observeBundlerTarget() {
    const target = document.getElementById(BundlerIntegration.targetId);
    if (!target || this.bundlerObserver) return;

    this.bundlerObserver = new MutationObserver(() => this.scheduleSync());
    this.bundlerObserver.observe(target, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Coalesce rapid DOM updates into a single sync per animation frame.
   */
  scheduleSync() {
    if (this.syncPending) return;
    this.syncPending = true;
    requestAnimationFrame(() => {
      this.syncPending = false;
      this.syncState();
    });
  }

  /**
   * Handles 'on:variant:change' from variant-picker.
   * @param {object} evt - Event object.
   */
  handleVariantChange(evt) {
    const productInfo = evt.detail?.form?.closest('.product-form[id^="ProductInfo-"]');
    if (productInfo !== this.productInfo) return;
    this.syncState();
  }
};

/**
 * Initialise bundler integration for each product info block on the page.
 */
function initBundlerIntegrations() {
  document.querySelectorAll('product-form.js-product .product-form[id^="ProductInfo-"]').forEach((productInfo) => {
    if (productInfo.dataset.bundlerIntegration) return;
    productInfo.dataset.bundlerIntegration = '';
    new BundlerIntegration(productInfo);
  });
}

document.addEventListener('DOMContentLoaded', initBundlerIntegrations);

if (window.Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', initBundlerIntegrations);
}
